import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { APP_NAME, ENGINE_LABELS } from "@/config";
import { useFlags } from "@/lib/flags";
import { useGitHub, connectGitHub } from "@/lib/github";
import { useCompanyName } from "@/lib/company";
import { Page, PageHeader, Card, StatusBadge, Segmented, Loading, Avatar, ago, int, usd, resources } from "@/components/common";
import { BorderTrail } from "@/components/Motion";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ConfirmDestructive from "@/components/ConfirmDestructive";
import { SHARES, investorLine, useInvestors, RemoveInvestorDialog } from "@/components/Investors";
import { DeleteAccountCard } from "@/components/AccountDeletion";
import BrandSettings from "@/components/BrandSettings";
import { cn } from "@/lib/utils";
import { ExternalLink, TriangleAlert, Check, EyeOff, UserPlus } from "lucide-react";

// design_handoff_crewbench/screens/Settings.dc.html: AI access, GitHub, engines and models, idle lock, session network,
// the workspace server and deleting the account, on one page with jump links. Investors (FEATURE_INVESTORS): what they
// see and who they are (design_handoff_team_roles/screens/Settings.dc.html; backend/investor_routes.py).
const PROVIDERS = {
  anthropic: { name: "Anthropic", engine: "claude", pool: "Anthropic workspace", placeholder: "sk-ant-..." },
  openai: { name: "OpenAI", engine: "codex", pool: "OpenAI project", placeholder: "sk-..." },
  // Muse Spark, through Codex (next features #11): your own Meta Model API key only, no credits
  meta: { name: "Meta", engine: "muse", optional: true, placeholder: "Meta Model API key" },
};
const NETWORK_MODES = [
  ["registries", "Package registries", "npm, PyPI, Go, Cargo, RubyGems and Maven, plus the hosts you add. Installs work; other sites are blocked."],
  ["off", "No internet", "Only the hosts you add, or nothing at all. For code that must not leave. Installs fail unless you add a registry."],
  ["open", "Open", "Any site. The agent could send your code anywhere. Not recommended."],
];
const LOCKS = [[300, "5 min"], [600, "10 min"], [900, "15 min"], [1800, "30 min"], [3600, "1 hour"]];
const RUNNER_KIND = { standard_vm: "Standard cloud server", cloud_vm: "Cloud server", dedicated: "Dedicated server", local_demo: "Local demo server" };
const PRICE_FIELDS = [["input", "Input"], ["output", "Output"], ["cache_read", "Cache read"], ["cache_write", "Cache write"]];
const per1m = (n) => (n == null ? "—" : `$${Number(n).toFixed(n < 1 ? 3 : 2)}`);
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "");
const JUMPS = [["ai", "AI access"], ["github", "GitHub"], ["engines", "Engines and models"], ["lock", "Idle lock"], ["network", "Session network"], ["mcp", "MCP servers"],
               ["connected-apps", "Connected apps"], ["investors", "Investors"], ["brand", "Brand"], ["server", "Workspace server"], ["delete", "Delete account"]];
function Investors({ inv, company }) {
  const qc = useQueryClient();
  const [removing, setRemoving] = useState(null);
  const save = useMutation({
    mutationFn: async (change) => (await api.put("/settings/investors", change)).data,
    onSuccess: (_d, change) => {
      qc.invalidateQueries({ queryKey: ["investor-settings"] });
      const [k, on] = Object.entries(change)[0];
      const label = SHARES.find(([id]) => id === k)[1];
      toast.success(`Investors ${on ? "now see" : "no longer see"} ${label}. Saved to the audit log.`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const shown = (k) => (save.isPending && k in save.variables ? save.variables[k] : inv.switches[k]);
  return (
    <Section id="investors" title="Investors" className="gap-[18px] max-w-[880px]" testid="settings-investors"
      note="Investors get a read-only dashboard and can write to you in Messages. Choose what they see; it applies to every investor at once."
      right={<Button asChild variant="outline"><Link to="/founder/team?invite=investor"><UserPlus />Invite an investor</Link></Button>}>
      <div className="flex flex-col border rounded-lg overflow-hidden">
        {SHARES.map(([k, label, help]) => {
          const locked = k === "hiring_costs" && !shown("money");
          const on = shown(k) && !locked;
          return (
            <div key={k} className={cn("flex items-start gap-3 px-3.5 py-3 border-t first:border-t-0", locked && "opacity-40")}>
              <Switch checked={on} disabled={locked || save.isPending} aria-label={label} className="mt-0.5" data-testid={`investor-share-${k}`}
                onCheckedChange={(v) => save.mutate({ [k]: v })} />
              <span className="flex-1 min-w-0 flex flex-col">
                <span className="font-semibold">{label}</span>
                <span className="text-sm text-muted-foreground [text-wrap:pretty]">{help}</span>
              </span>
              <span className={cn("text-xs uppercase tracking-[0.09em] whitespace-nowrap", locked ? "text-gr-warning" : on ? "text-gr-success" : "text-muted-foreground")}>
                {locked ? "Needs Money" : on ? "Shared" : "Not shared"}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex flex-col gap-1.5 text-sm text-muted-foreground">
        <p className="m-0 flex gap-2 items-start"><Check className="w-3.5 h-3.5 mt-0.5 shrink-0" />Always shared: progress (objectives and change report counts) and your team's names, titles and kinds of work.</p>
        <p className="m-0 flex gap-2 items-start"><EyeOff className="w-3.5 h-3.5 mt-0.5 shrink-0" />Investors never see sessions, change report summaries, your messages with your team, or your settings.</p>
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-xs uppercase tracking-[0.09em] text-muted-foreground">Investors · {inv.investors.length}</span>
        {inv.investors.length ? inv.investors.map((i) => (
          <div key={i.id} className="flex flex-wrap items-center gap-3 px-3.5 py-3 border rounded-lg" data-testid="investor-row">
            <Avatar name={i.name} size={28} />
            <span className="flex-1 min-w-[180px] flex flex-col">
              <span className="font-semibold">{i.name}</span>
              <span className="text-sm text-muted-foreground">{[i.email, i.joined_at && `Joined ${day(i.joined_at)}`, `Weekly summary ${i.weekly_email ? "on" : "off"}`].filter(Boolean).join(" · ")}</span>
            </span>
            <Button variant="danger" onClick={() => setRemoving(i)} data-testid={`investor-remove-${i.id}`}>Remove</Button>
          </div>
        )) : <p className="m-0 px-3.5 py-3 border rounded-lg text-muted-foreground">No investors. Invite one from Team.</p>}
      </div>
      <p className="m-0 text-sm text-muted-foreground">Saved as you switch. Every change, invite and removal goes in the audit log.</p>
      <RemoveInvestorDialog investor={removing} company={company} onClose={() => setRemoving(null)} />
    </Section>
  );
}

function Section({ id, title, note, right, children, className, testid }) {
  return (
    <Card id={id} className={cn("scroll-mt-4 px-5 py-[18px] flex flex-col gap-3", className)} data-testid={testid}>
      <div className="flex justify-between items-start gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="font-display text-[15px] tracking-[0.02em] font-semibold">{title}</span>
          {note && <span className="text-sm text-muted-foreground [text-wrap:pretty]">{note}</span>}
        </div>
        {right}
      </div>
      {children}
    </Card>
  );
}

// a key field and Add key (8 characters at least); the key is stored encrypted and never shown again
function AddKey({ provider, name, placeholder, onAdded }) {
  const qc = useQueryClient();
  const [key, setKey] = useState("");
  const add = useMutation({
    mutationFn: async () => api.post("/settings/api-keys", { provider, key: key.trim() }),
    onSuccess: () => {
      setKey(""); ["settings", "ai-access", "engines", "onboarding"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success(`${name} key stored, encrypted.`); onAdded?.();
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const ready = key.trim().length >= 8;
  return (
    <form className="flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); if (ready && !add.isPending) add.mutate(); }}>
      <input type="password" aria-label={`${name} API key`} placeholder={placeholder} value={key} onChange={(e) => setKey(e.target.value)}
        autoComplete="off" data-testid={`apikey-input-${provider}`}
        className="flex-[1_1_220px] h-8 px-2.5 rounded-md border border-gr-control-border bg-transparent font-mono text-[12px] placeholder:text-muted-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring" />
      <Button type="submit" aria-disabled={!ready} disabled={add.isPending} data-testid={`apikey-add-${provider}`}>Add key</Button>
    </form>
  );
}

function AiAccess({ settings, company, onDelete }) {
  const qc = useQueryClient();
  const flags = useFlags();
  const { data: access } = useQuery({ queryKey: ["ai-access"], enabled: !!flags.store, queryFn: async () => (await api.get("/settings/ai-access")).data });
  const choose = useMutation({
    mutationFn: async ({ provider, mode }) => api.put("/settings/ai-access", { provider, mode }),
    onSuccess: (_r, { provider, mode }) => {
      ["ai-access", "engines"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success(mode === "credits" ? `${PROVIDERS[provider].name} sessions now use ${APP_NAME} credits.` : `${PROVIDERS[provider].name} sessions now use your own key.`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const keys = Object.fromEntries(settings.api_keys.map((k) => [k.provider, k]));
  const modes = Object.fromEntries((access?.providers || []).map((p) => [p.provider, p]));
  const onCredits = (access?.providers || []).filter((p) => p.mode === "credits").map((p) => PROVIDERS[p.provider].engine);
  const prices = (access?.models || []).filter((m) => onCredits.includes(m.engine));
  const balance = access?.account?.balance_usd || 0;
  return (
    <Section id="ai" title="AI access" testid="ai-access-card"
      note={flags.store ? `For each provider, sessions use either your own API key or ${APP_NAME} credits. Keys are stored encrypted and never shown again.`
        : "Sessions use your own API key for each provider. Keys are stored encrypted and never shown again."}>
      {Object.entries(PROVIDERS).map(([id, p]) => {
        const credits = modes[id]?.mode === "credits";
        const key = keys[id];
        return (
          <div key={id} className="flex flex-wrap items-center justify-between gap-x-5 gap-y-3 py-3.5 border-t" data-testid={`ai-access-${id}`}>
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="font-semibold">{p.name}</span>
              {credits ? (
                <span className="text-sm text-muted-foreground">{APP_NAME} credits, through {company ? `${company}'s` : "your"} own {p.pool}. Usage is charged to your credit balance.</span>
              ) : key ? (
                <span className="text-sm text-muted-foreground" data-testid={`apikey-${id}`}>Your key <span className="font-mono">•••• {key.last4}</span>{key.created_at && `, added ${day(key.created_at)}`}</span>
              ) : <span className="text-sm text-muted-foreground">No key yet.</span>}
              {credits && modes[id] && !modes[id].credits_available && <span className="text-sm text-gr-warning">{APP_NAME} credits aren't available right now.</span>}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {flags.store && modes[id] && (
                <Segmented label={`${p.name} access`} options={[["own_key", "Own key"], ["credits", "Credits"]]} value={modes[id].mode}
                  onChange={(mode) => mode !== modes[id].mode && choose.mutate({ provider: id, mode })} testid={`ai-access-${id}-mode`} />
              )}
              {key && <button type="button" onClick={() => onDelete(id)} className="text-sm text-muted-foreground underline underline-offset-2 hover:opacity-50"
                data-testid={`apikey-del-${id}`}>Delete key</button>}
            </div>
            {!credits && !key && (
              <div className="basis-full flex flex-col gap-2">
                <AddKey provider={id} name={p.name} placeholder={p.placeholder} />
                {p.optional ? (
                  <span className="text-sm text-muted-foreground [text-wrap:pretty]">Only for sessions on {ENGINE_LABELS[p.engine]}, with your own key from Meta's
                    Model API. Meta doesn't train on what's sent on it; its "contributor" models, which do, are never offered.</span>
                ) : <span className="text-sm text-gr-warning">Sessions on {ENGINE_LABELS[p.engine]} can't run until you add a key{flags.store ? " or switch to credits" : ""}.</span>}
              </div>
            )}
          </div>
        );
      })}
      {onCredits.length > 0 && balance < (access?.floor_usd ?? 0) && (
        <span className="flex items-start gap-1.5 text-sm text-gr-warning" data-testid="credits-out">
          <TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />Out of credits: sessions on credits stop until credits are added in Store.
        </span>
      )}
      {prices.length > 0 && (
        <div className="flex flex-col gap-2 pt-3.5 border-t">
          <span className="text-sm text-muted-foreground">Your prices on credits, per 1M tokens (the provider's price + {access.markup_pct}%). Balance: {usd(balance)}.</span>
          <div className="border rounded-lg overflow-x-auto text-sm tabular-nums" data-testid="credit-prices">
            <div className="min-w-[560px] grid grid-cols-[minmax(0,1fr)_repeat(4,90px)] gap-3 px-3.5 py-2 text-xs uppercase tracking-[0.09em] text-muted-foreground border-b">
              <span>Model</span>{PRICE_FIELDS.map(([k, l]) => <span key={k} className="text-right">{l}</span>)}
            </div>
            {prices.map((m) => (
              <div key={m.model_id} className="min-w-[560px] grid grid-cols-[minmax(0,1fr)_repeat(4,90px)] gap-3 px-3.5 py-2 border-b last:border-b-0">
                <span className="min-w-0 truncate">{m.label} <span className="font-mono text-muted-foreground">{m.model_id}</span></span>
                {PRICE_FIELDS.map(([k]) => <span key={k} className="text-right">{per1m(m.credit_prices_per_1m[k])}</span>)}
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

// Connecting GitHub installs the platform's GitHub App on the repositories the founder picks (a popup). Without it, a
// GitHub token opens the pull requests (code is pushed with each project's deploy key).
function GitHub({ settings, onDelete }) {
  const qc = useQueryClient();
  const flags = useFlags();
  const { available, connection } = useGitHub();
  const [asking, setAsking] = useState(false);
  const disconnect = useMutation({
    mutationFn: async (password) => api.delete("/github", { data: { password } }),
    onSuccess: () => { ["github", "projects"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); setAsking(false); toast.success("GitHub disconnected."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  if (!flags.github_remotes) return null;
  const token = settings.api_keys.find((k) => k.provider === "github");
  return (
    <Section id="github" title="GitHub" className="gap-2.5" testid="github-card">
      {connection ? (
        <div className="flex flex-col gap-2.5" data-testid="github-connected">
          <span>Connected to <span className="font-semibold">{connection.account}</span>
            <span className="text-sm text-muted-foreground"> · {connection.account_type === "Organization" ? "organization" : "personal account"} · {connection.repository_selection === "all" ? "all repositories" : "selected repositories"}</span></span>
          <span className="text-sm text-gr-ink-secondary [text-wrap:pretty]">Sessions push their branches and submits open pull requests through the {APP_NAME} app, with short-lived tokens for one repository at a time. No deploy keys or tokens to manage.</span>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Button variant="outline" asChild><a href={connection.manage_url} target="_blank" rel="noreferrer"><ExternalLink />Choose repositories</a></Button>
            <button type="button" onClick={() => setAsking(true)} className="text-sm text-muted-foreground underline underline-offset-2 hover:opacity-50" data-testid="github-disconnect">Disconnect</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2.5 items-start">
          {available && <>
            <span className="text-sm text-gr-ink-secondary">Let {APP_NAME} push session branches and open pull requests in the repositories you choose. Until then, projects use deploy keys.</span>
            <Button onClick={connectGitHub} data-testid="github-connect-btn">Connect GitHub</Button>
          </>}
          <div className={cn("w-full flex flex-col gap-2", available && "pt-3 mt-1 border-t")}>
            <span className="text-sm text-gr-ink-secondary [text-wrap:pretty]">
              {available ? "Or, without the app: a" : "A"} GitHub token opens pull requests when your team submits. Use a fine-grained token for your repositories
              with "Contents: read-only" and "Pull requests: read and write". Code is pushed with each project's deploy key, not this token.
            </span>
            {token ? (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm" data-testid="apikey-github">
                <span className="text-muted-foreground">Your token <span className="font-mono">•••• {token.last4}</span>{token.created_at && `, added ${day(token.created_at)}`}</span>
                <button type="button" onClick={() => onDelete("github")} className="text-muted-foreground underline underline-offset-2 hover:opacity-50" data-testid="apikey-del-github">Delete token</button>
              </div>
            ) : <AddKey provider="github" name="GitHub" placeholder="github_pat_..." />}
          </div>
        </div>
      )}
      <ConfirmDestructive open={asking} onOpenChange={setAsking} title="Disconnect GitHub?" action="Disconnect"
        pending={disconnect.isPending} onConfirm={(password) => disconnect.mutate(password)}>
        <p className="m-0 [text-wrap:pretty]">Projects go back to deploy keys until you connect again. Meanwhile sessions can't push or open pull requests through the {APP_NAME} app.
          The app stays installed on GitHub until you remove it there.</p>
      </ConfirmDestructive>
    </Section>
  );
}

function EnginesAndModels({ settings }) {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: async (body) => api.put("/settings", body),
    onSuccess: (_r, { message }) => { ["settings", "engines"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); toast.success(message); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const engines = settings.allowed_engines;
  const models = settings.allowed_models;
  const toggleEngine = (e) => {
    const on = engines.includes(e);
    save.mutate({ allowed_engines: on ? engines.filter((x) => x !== e) : [...engines, e],
                  message: `${ENGINE_LABELS[e] || e} ${on ? "turned off for new sessions" : "allowed"}.` });
  };
  const toggleModel = (m) => {
    const on = models.includes(m.model_id);
    save.mutate({ allowed_models: on ? models.filter((x) => x !== m.model_id) : [...models, m.model_id],
                  message: `${m.label} ${on ? "turned off for new sessions" : "allowed"}.` });
  };
  return (
    <Section id="engines" title="Engines and models" className="gap-3.5" testid="settings-access-card"
      note="What your team can start sessions with. Every request is checked against this.">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
        {settings.engines.map((e) => (
          <div key={e} className="flex flex-col gap-2">
            {/* label beside, not around, the checkbox: a wrapping label clicks it a second time */}
            <div className="flex items-center gap-2 font-semibold">
              <Checkbox id={`allow-engine-${e}`} checked={engines.includes(e)} disabled={save.isPending} onCheckedChange={() => toggleEngine(e)} data-testid={`allow-engine-${e}`} />
              <label htmlFor={`allow-engine-${e}`} className="cursor-pointer">{ENGINE_LABELS[e] || e}</label>
            </div>
            {settings.models.filter((m) => m.engine === e).map((m) => (
              <div key={m.model_id} className="flex items-center gap-2 pl-6 text-sm">
                <Checkbox id={`allow-model-${m.model_id}`} checked={models.includes(m.model_id)} disabled={!engines.includes(e) || save.isPending}
                  onCheckedChange={() => toggleModel(m)} data-testid={`allow-model-${m.model_id}`} />
                <label htmlFor={`allow-model-${m.model_id}`} className="cursor-pointer">{m.label} <span className="font-mono text-muted-foreground">{m.model_id}</span></label>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Section>
  );
}

function IdleLock({ settings }) {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: async (seconds) => api.put("/settings", { idle_lock_seconds: seconds }),
    onSuccess: (_r, seconds) => {
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success(`Sessions now lock after ${seconds === 3600 ? "1 hour" : `${seconds / 60} minutes`} without activity.`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const current = settings.idle_lock_seconds;
  const custom = !LOCKS.some(([s]) => s === current);
  return (
    <Section id="lock" title="Idle lock"
      note="A session locks when nobody uses it for this long. Locking stops the engine and backs up its work; unlocking needs the person's password or passkey.">
      <Segmented label="Idle lock" className="self-start" options={LOCKS} value={current} onChange={(s) => s !== current && save.mutate(s)} testid="idle-lock" />
      {custom && <span className="text-sm text-muted-foreground">Now: after {int(Math.round(current / 60))} minutes.</span>}
    </Section>
  );
}

// a radio, as the handoff draws it: a 1px ring when off, a 5px one when on
function Radio({ on }) {
  return <span aria-hidden="true" className={cn("w-4 h-4 mt-0.5 rounded-full shrink-0 box-border", on ? "border-[5px] border-foreground" : "border border-gr-control-border")} />;
}

// What session containers may reach (plan Step 6). The metering proxy is always reachable.
function SessionNetwork({ settings }) {
  const qc = useQueryClient();
  const savedHosts = (settings.egress_allow || []).join("\n");
  const [mode, setMode] = useState(settings.session_network);
  const [hosts, setHosts] = useState(savedHosts);
  const [search, setSearch] = useState(settings.web_search);
  const [docs, setDocs] = useState(settings.docs_sites);
  const [showDocs, setShowDocs] = useState(false);
  useEffect(() => {
    setMode(settings.session_network); setHosts(savedHosts); setSearch(settings.web_search); setDocs(settings.docs_sites);
  }, [settings.session_network, savedHosts, settings.web_search, settings.docs_sites]);
  const dirty = mode !== settings.session_network || search !== settings.web_search || docs !== settings.docs_sites
    || hosts.split(/[\s,]+/).filter(Boolean).join("\n") !== savedHosts;
  const save = useMutation({
    mutationFn: async () => api.put("/settings", { session_network: mode, egress_allow: hosts.split(/[\s,]+/).filter(Boolean),
      web_search: search, docs_sites: docs }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); toast.success("Session network saved. It applies from each session's next unlock."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <Section id="network" title="Session network" className="gap-3.5" testid="settings-network-card"
      note={`What sessions can reach on the internet. They always reach ${APP_NAME}'s metering proxy. Everything else not allowed here is blocked, and IP addresses and private networks always are.`}>
      <div className="flex flex-wrap gap-5">
        <div role="radiogroup" aria-label="Session network" className="flex-[1_1_280px] flex flex-col gap-2.5" data-testid="network-mode">
          {NETWORK_MODES.map(([value, label, help]) => (
            <button key={value} type="button" role="radio" aria-checked={mode === value} onClick={() => setMode(value)} data-testid={`network-${value}`}
              className="flex items-start gap-2.5 text-left rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
              <Radio on={mode === value} />
              <span className="flex flex-col"><span className="font-semibold">{label}</span><span className="text-sm text-muted-foreground [text-wrap:pretty]">{help}</span></span>
            </button>
          ))}
        </div>
        <div className="flex-[1_1_280px] flex flex-col gap-1.5">
          <label htmlFor="egress-hosts" className="text-sm font-semibold">Also allow these hosts (HTTPS)</label>
          <Textarea id="egress-hosts" rows={4} className="font-mono text-[12px] leading-[18px] px-2.5 py-2" value={hosts} disabled={mode === "open"}
            onChange={(e) => setHosts(e.target.value)} placeholder="git.yourcompany.dev" data-testid="egress-hosts" />
          <span className="text-sm text-muted-foreground [text-wrap:pretty]">
            {mode === "open" ? "Not needed: Open allows every site."
              : "One per line. *.example.com allows its subdomains. Registries accept uploads too, so choose No internet for code that must never leave."}
          </span>
        </div>
      </div>
      {/* backend/session_network.DOCS_HOSTS: read-only documentation sites */}
      <label className="flex items-start gap-2.5 cursor-pointer">
        <Switch checked={!!docs && mode !== "open"} disabled={mode === "open"} onCheckedChange={setDocs} className="mt-0.5" data-testid="network-docs" />
        <span className="flex flex-col"><span className="font-semibold">Documentation sites</span>
          <span className="text-sm text-muted-foreground [text-wrap:pretty]">
            {mode === "open" ? "Not needed: Open allows every site." : <>The agent can read the official docs of the languages
              and tools it builds with ({(settings.docs_hosts || []).length} sites, read-only). Off by default with No internet.{" "}
              <button type="button" className="underline underline-offset-2" onClick={(e) => { e.preventDefault(); setShowDocs(!showDocs); }}
                data-testid="network-docs-list">{showDocs ? "Hide the list" : "See the list"}</button></>}
          </span>
          {showDocs && mode !== "open" && (
            <span className="mt-1.5 font-mono text-[12px] leading-[18px] text-muted-foreground [overflow-wrap:anywhere]">{(settings.docs_hosts || []).join(" · ")}</span>
          )}
        </span>
      </label>
      {/* backend/session_network.web_search: the providers' own search runs outside this setting */}
      <label className="flex items-start gap-2.5 cursor-pointer">
        <Switch checked={!!search} onCheckedChange={setSearch} className="mt-0.5" data-testid="network-web-search" />
        <span className="flex flex-col"><span className="font-semibold">Web search by Anthropic and OpenAI</span>
          <span className="text-sm text-muted-foreground [text-wrap:pretty]">
            The agent can search the web through the AI provider. Searches run at the provider, outside the network
            setting above, and send the query text out; each one is charged on top of tokens, like them. Off by
            default with No internet. Claude Agent only for now: Codex doesn't offer its web search through
            Crewbench's metering, so Codex sessions read pages from the terminal instead, within the setting above.
          </span></span>
      </label>
      <div className="flex flex-wrap items-center justify-end gap-3">
        <span className="text-sm text-muted-foreground">Applies from each session's next unlock.</span>
        <Button aria-disabled={!dirty} disabled={save.isPending} onClick={() => dirty && save.mutate()} data-testid="network-save">Save network</Button>
      </div>
    </Section>
  );
}

// MCP servers (next features #9, backend/mcp_servers.py): the only ones sessions get, for both engines alike. Their
// secrets are write-only: stored encrypted, never shown again.
const EMPTY_MCP = { name: "", transport: "stdio", command: "", args: "", url: "", secrets: "", enabled: true };
const lines = (text) => text.split("\n").map((x) => x.trim()).filter(Boolean);

function McpServers() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["mcp-servers"], queryFn: async () => (await api.get("/mcp-servers")).data });
  const [form, setForm] = useState(null);  // {id?, ...EMPTY_MCP}
  const refresh = () => qc.invalidateQueries({ queryKey: ["mcp-servers"] });
  const save = useMutation({
    mutationFn: async (f) => {
      const secrets = f.secrets.trim() ? Object.fromEntries(lines(f.secrets).map((l) => {
        const at = l.search(/[=:]/);
        return at < 0 ? [l, ""] : [l.slice(0, at).trim(), l.slice(at + 1).trim()];
      })) : (f.id ? null : {});
      const body = { name: f.name.trim(), transport: f.transport, command: f.command.trim() || null, args: lines(f.args),
        url: f.url.trim() || null, secrets, enabled: f.enabled };
      return f.id ? api.put(`/mcp-servers/${f.id}`, body) : api.post("/mcp-servers", body);
    },
    onSuccess: () => { refresh(); setForm(null); toast.success("Saved. Sessions get it from their next unlock."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const toggle = useMutation({
    mutationFn: async (s) => api.put(`/mcp-servers/${s.id}`, { name: s.name, transport: s.transport, command: s.command, args: s.args,
      url: s.url, secrets: null, enabled: !s.enabled }),
    onSuccess: refresh, onError: (e) => toast.error(apiErr(e)),
  });
  const remove = useMutation({
    mutationFn: async (s) => api.delete(`/mcp-servers/${s.id}`),
    onSuccess: () => { refresh(); toast.success("Removed. Sessions lose it from their next unlock."); }, onError: (e) => toast.error(apiErr(e)),
  });
  const servers = data?.servers || [];
  const f = form;
  return (
    <Section id="mcp" title="MCP servers" className="gap-3" testid="settings-mcp-card"
      note="Tools your sessions' agents can use, on both engines. Only the servers listed here: never one a repository brings. They run inside each session's network rules, and what they return is metered as model tokens. A turn waits for each server to start and goes on without one that can't (under Codex, the audit log says why)."
      right={<Button size="sm" variant="outline" onClick={() => setForm({ ...EMPTY_MCP })} data-testid="mcp-add">Add server</Button>}>
      {servers.length === 0 ? <span className="text-sm text-muted-foreground">None yet.</span> : servers.map((s) => (
        <div key={s.id} className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2 py-2.5 border-t" data-testid={`mcp-row-${s.name}`}>
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="font-semibold">{s.name}</span>
            <span className="font-mono text-[12px] text-muted-foreground [overflow-wrap:anywhere]">
              {s.transport === "stdio" ? [s.command, ...s.args].join(" ") : s.url}</span>
            {s.secret_names.length > 0 && <span className="text-sm text-muted-foreground">{s.transport === "stdio" ? "Variables" : "Headers"}: {s.secret_names.join(", ")}</span>}
            {s.reachable === false && <span className="text-sm text-gr-warning flex items-start gap-1.5"><TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Its host isn't allowed in Session network: sessions can't reach it.</span>}
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={s.enabled} onCheckedChange={() => toggle.mutate(s)} aria-label={`${s.name} on`} data-testid={`mcp-enabled-${s.name}`} />
            <button type="button" className="text-sm underline underline-offset-2 hover:opacity-50" onClick={() => setForm({
              id: s.id, name: s.name, transport: s.transport, command: s.command || "", args: (s.args || []).join("\n"), url: s.url || "",
              secrets: "", enabled: s.enabled, stored: s.secret_names })}>Edit</button>
            <button type="button" className="text-sm text-muted-foreground underline underline-offset-2 hover:opacity-50" onClick={() => remove.mutate(s)}
              data-testid={`mcp-remove-${s.name}`}>Remove</button>
          </div>
        </div>
      ))}
      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="gap-[18px]">
          <div className="flex flex-col gap-1.5">
            <DialogTitle>{f?.id ? `Change ${f.name}` : "Add an MCP server"}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground [text-wrap:pretty]">
              A command the engine starts in the session's container, or a URL it connects to. The session's agent can read its variables or
              headers (they're its tools' credentials): give it the narrowest token that does the job.
            </DialogDescription>
          </div>
          {f && (
            <form className="flex flex-col gap-3.5" onSubmit={(e) => { e.preventDefault(); save.mutate(f); }}>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="mcp-name">Name</Label>
                <Input id="mcp-name" className="font-mono text-[12px]" placeholder="e.g. sentry" value={f.name} onChange={(e) => setForm({ ...f, name: e.target.value })} data-testid="mcp-name" />
              </div>
              <Segmented label="Kind" className="self-start" options={[["stdio", "Command"], ["http", "URL"]]} value={f.transport}
                onChange={(v) => setForm({ ...f, transport: v })} testid="mcp-transport" />
              {f.transport === "stdio" ? (<>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="mcp-command">Command</Label>
                  <Input id="mcp-command" className="font-mono text-[12px]" placeholder="npx" value={f.command} onChange={(e) => setForm({ ...f, command: e.target.value })} data-testid="mcp-command" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="mcp-args">Arguments, one per line</Label>
                  <Textarea id="mcp-args" rows={3} className="font-mono text-[12px]" placeholder={"-y\n@scope/some-mcp-server"} value={f.args}
                    onChange={(e) => setForm({ ...f, args: e.target.value })} data-testid="mcp-args" />
                  <span className="text-sm text-muted-foreground">Its download needs the package registries in Session network.</span>
                </div>
              </>) : (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="mcp-url">URL</Label>
                  <Input id="mcp-url" className="font-mono text-[12px]" placeholder="https://mcp.example.com/mcp" value={f.url} onChange={(e) => setForm({ ...f, url: e.target.value })} data-testid="mcp-url" />
                  <span className="text-sm text-muted-foreground">Its host must be allowed in Session network.</span>
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="mcp-secrets">{f.transport === "stdio" ? "Environment variables" : "Headers"}, one per line as NAME=value</Label>
                <Textarea id="mcp-secrets" rows={3} className="font-mono text-[12px]" placeholder={f.transport === "stdio" ? "API_TOKEN=..." : "Authorization=Bearer ..."}
                  value={f.secrets} onChange={(e) => setForm({ ...f, secrets: e.target.value })} data-testid="mcp-secrets" />
                <span className="text-sm text-muted-foreground">{f.stored?.length ? `Stored: ${f.stored.join(", ")}. Leave this empty to keep them; anything entered replaces them all.`
                  : "Stored encrypted, never shown again."}</span>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
                <Button type="submit" disabled={save.isPending} data-testid="mcp-save">Save</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Section>
  );
}

// The connector (FEATURE_CONNECTOR, backend/connector_oauth.py): the link this founder pastes into their own AI app, and
// the apps they allowed to read Crewbench. Each founder sees and disconnects their own; disconnecting asks the password.
const CONNECT_HOWTO = [
  ["Claude and Claude Duet", "Settings → Connectors → Add custom connector, then paste the link."],
  ["ChatGPT and its Dots", "Settings → Apps → Create (developer mode), then paste the link."],
  ["Grok Bot", "Connectors → Add a custom MCP server, then paste the link."],
  ["Claude Code", "claude mcp add --transport http crewbench <link>"],
  ["Cursor", "Settings → MCP → Add a remote server, then paste the link."],
];

function ConnectedApps() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["connector-apps"], queryFn: async () => (await api.get("/connector/apps")).data });
  const [removing, setRemoving] = useState(null);
  const disconnect = useMutation({
    mutationFn: async ({ id, password }) => api.post(`/connector/apps/${id}/disconnect`, { password }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["connector-apps"] }); setRemoving(null); toast.success("Disconnected. It can't read anything any more."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const copy = async () => {
    try { await navigator.clipboard.writeText(data.url); toast.success("Link copied"); } catch { toast.error("Couldn't copy: select the link instead."); }
  };
  const apps = data?.apps || [];
  return (
    <Section id="connected-apps" title="Connected apps" className="gap-3" testid="settings-connected-apps"
      note={`Let your own AI app read ${APP_NAME}: Claude (and Claude Duet), ChatGPT (and its Dots), Grok Bot, Claude Code or Cursor. Paste this link into it and allow it here. It reads your week, objectives, money, budgets, reliability, delivery times, team, usage and audit log. It can't change anything, and never sees your team's sessions or messages.`}>
      {data && (
        <div className="flex flex-wrap items-center gap-2">
          <code className="flex-1 min-w-[220px] px-3 py-2 border rounded-lg font-mono text-[12px] [overflow-wrap:anywhere] select-all" data-testid="connector-url">{data.url}</code>
          <Button size="sm" variant="outline" onClick={copy} data-testid="connector-copy">Copy link</Button>
        </div>
      )}
      <details className="text-sm">
        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Where to paste it</summary>
        <div className="flex flex-col gap-1.5 pt-2">
          {CONNECT_HOWTO.map(([app, how]) => (
            <p key={app} className="m-0 [text-wrap:pretty]"><span className="font-semibold">{app}:</span>{" "}
              <span className={app === "Claude Code" ? "font-mono text-[12px]" : "text-muted-foreground"}>{how}</span></p>
          ))}
          <p className="m-0 text-muted-foreground">The names of menus change often; look for "connector", "MCP" or "custom app".</p>
        </div>
      </details>
      <div className="flex flex-col gap-2">
        <span className="text-xs uppercase tracking-[0.09em] text-muted-foreground">Your connected apps · {apps.length}</span>
        {apps.length === 0 ? <span className="text-sm text-muted-foreground">None yet.</span> : apps.map((a) => (
          <div key={a.id} className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2 px-3.5 py-3 border rounded-lg" data-testid="connected-app-row">
            <span className="flex flex-col gap-0.5 min-w-0">
              <span className="font-semibold [overflow-wrap:anywhere]">{a.app}</span>
              <span className="text-sm text-muted-foreground">
                {[a.returns_to?.kind === "web" ? a.returns_to.host : "An app on your computer", `Connected ${day(a.created_at)}`,
                  a.last_used_at ? `Last read ${ago(a.last_used_at)}` : "Not used yet"].join(" · ")}</span>
            </span>
            <Button variant="danger" size="sm" onClick={() => setRemoving(a)} data-testid={`connected-app-remove-${a.id}`}>Disconnect</Button>
          </div>
        ))}
      </div>
      <ConfirmDestructive open={!!removing} onOpenChange={(v) => !v && setRemoving(null)} action="Disconnect"
        title={`Disconnect ${removing?.app}?`} pending={disconnect.isPending}
        onConfirm={(password) => disconnect.mutate({ id: removing.id, password })}>
        <p className="m-0 [text-wrap:pretty]">It can't read {APP_NAME} any more. To use it again, connect it again from the app.</p>
      </ConfirmDestructive>
    </Section>
  );
}

function WorkspaceServer({ company }) {
  const { data: runners } = useQuery({ queryKey: ["runners"], queryFn: async () => (await api.get("/runners")).data, refetchInterval: 5000 });
  const r = runners?.runners?.[0];
  const stats = r && [
    ["Size", RUNNER_KIND[r.kind] || r.kind],
    ["Sessions running", r.max_active_sessions ? `${int(r.active_sessions)} of ${int(r.max_active_sessions)}` : int(r.active_sessions)],
    ["Each session", resources(r.session_resources)],
    ["Last heartbeat", ago(r.last_heartbeat_at)],
    ["CPU", `${Math.round(r.cpu || 0)}%`],
    ["Memory", `${Math.round(r.memory || 0)}%`],
    ["Version", r.version || "—"],
  ];
  return (
    <Section id="server" title="Workspace server" className="relative gap-3.5" testid="settings-runner-card" right={r && <StatusBadge status={r.status} />}>
      {r?.status === "online" && <BorderTrail />}
      {r ? (
        <div className="flex flex-wrap gap-px bg-gr-hairline border rounded-lg overflow-hidden tabular-nums">
          {stats.map(([label, value]) => (
            <div key={label} className="flex-[1_1_140px] bg-card px-3.5 py-2.5 flex flex-col gap-0.5">
              <span className="text-xs uppercase tracking-[0.09em] text-muted-foreground">{label}</span><span>{value}</span>
            </div>
          ))}
        </div>
      ) : <span className="text-sm text-muted-foreground">No workspace server yet.</span>}
      <span className="text-sm text-muted-foreground">View only. {company ? `${company}'s` : "Your"} sessions run only on this server, and founders never get shell access to it.</span>
    </Section>
  );
}

export default function Settings() {
  const qc = useQueryClient();
  const flags = useFlags();
  const company = useCompanyName();
  const { data, isLoading } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { data: access } = useQuery({ queryKey: ["ai-access"], enabled: !!flags.store, queryFn: async () => (await api.get("/settings/ai-access")).data });
  const inv = useInvestors();  // FEATURE_INVESTORS: the header's line and the Investors card
  const [removing, setRemoving] = useState(null);  // the provider whose key is being deleted
  const delKey = useMutation({
    mutationFn: async ({ provider, password }) => api.delete(`/settings/api-keys/${provider}`, { data: { password } }),
    onSuccess: (_r, { provider }) => {
      ["settings", "ai-access", "engines"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      setRemoving(null); toast.success(`${PROVIDERS[provider]?.name || "GitHub"} ${provider === "github" ? "token" : "key"} deleted.`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const eyebrow = ["Settings", company].filter(Boolean).join(" · ");
  const title = flags.investors ? "Keys, engines, network, investors and your workspace server." : "Keys, engines, network and your workspace server.";
  if (isLoading || !data) return <Page className="gap-5"><PageHeader eyebrow={eyebrow} title={title} /><Loading /></Page>;

  // "your Anthropic key", "Crewbench credits for OpenAI" or "no OpenAI access yet"
  const how = (id) => {
    const name = PROVIDERS[id].name;
    if (access?.providers?.find((p) => p.provider === id)?.mode === "credits") return `${APP_NAME} credits for ${name}`;
    return data.api_keys.some((k) => k.provider === id) ? `your ${name} key` : `no ${name} access yet`;
  };
  const jumps = JUMPS.filter(([id]) => (id !== "github" || flags.github_remotes) && (id !== "investors" || flags.investors)
    && (id !== "brand" || flags.documents) && (id !== "connected-apps" || flags.connector));
  const removingName = removing === "github" ? "GitHub token" : `${PROVIDERS[removing]?.name} key`;
  return (
    <Page className="gap-5" data-testid="owner-settings">
      <div className="flex flex-col gap-1.5">
        <PageHeader eyebrow={eyebrow} title={title}
          status={flags.investors && inv ? investorLine(inv) : `Sessions run on ${how("anthropic")} and ${how("openai")}.`} />
        <nav className="flex flex-wrap gap-x-4 gap-y-1.5 pt-1.5 text-sm" aria-label="On this page">
          {jumps.map(([id, label]) => (
            <a key={id} href={`#${id}`} className="inline-flex items-center gap-1.5 hover:opacity-50">
              <span className="underline underline-offset-2">{label}</span>
            </a>
          ))}
        </nav>
      </div>
      <AiAccess settings={data} company={company} onDelete={setRemoving} />
      <GitHub settings={data} onDelete={setRemoving} />
      <EnginesAndModels settings={data} />
      <IdleLock settings={data} />
      <SessionNetwork settings={data} />
      <McpServers />
      {flags.connector && <ConnectedApps />}
      {flags.investors && inv && <Investors inv={inv} company={company} />}
      {flags.documents && <BrandSettings />}
      <WorkspaceServer company={company} />
      <div id="delete" className="scroll-mt-4"><DeleteAccountCard /></div>
      <ConfirmDestructive open={!!removing} onOpenChange={(v) => !v && setRemoving(null)} action={removing === "github" ? "Delete token" : "Delete key"}
        title={`Delete your ${removingName}?`} pending={delKey.isPending} onConfirm={(password) => delKey.mutate({ provider: removing, password })}>
        <p className="m-0 [text-wrap:pretty]">{removing === "github"
          ? `It's deleted from ${APP_NAME}. Submitting a session can't open a pull request until you add a token again or connect GitHub.`
          : `It's deleted from ${APP_NAME}. Sessions on ${ENGINE_LABELS[PROVIDERS[removing]?.engine]} stop working until you add a key again${flags.store ? ` or switch to ${APP_NAME} credits` : ""}.`}</p>
      </ConfirmDestructive>
    </Page>
  );
}
